import React, { useDeferredValue, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { FiChevronLeft, FiEyeOff, FiRefreshCw, FiSearch, FiX } from 'react-icons/fi';
import { Helmet } from 'react-helmet-async';
import PageLayout from '../../components/layout/PageLayout';
import SEO from '../../components/common/SEO';
import { AccountBoundary, useRequestScope, useSettings } from '../../features/settings/shared';
import { useMemberEntry } from '../../features/members/memberMemory';
import { restoreSearchScroll } from '../../features/search/useSearchScrollMemory';
import { feedInitialPage, loadFeedPage, uniqueFeeds } from './feedListModel';
import FeedListCard from './FeedListCard';
import { feedsApi } from '../../api/feeds';
import './FeedList.css';

export default function FeedList() {
  return <AccountBoundary component={FeedBoundary} />;
}
function FeedBoundary({ user }) {
  const { locale } = useSettings();
  const [params] = useSearchParams();
  const hidden = Boolean(user?.id && user.role === 'ADMIN' && params.get('hidden') === '1');
  const memory = useMemberEntry(`feed-list:${locale}:${hidden ? 'hidden' : 'public'}`);
  return (
    <FeedListContent
      key={`${locale}:${memory.entry.version}:${hidden}`}
      {...memory}
      {...{ user, locale, hidden }}
    />
  );
}
function FeedListContent({ user, locale, hidden, entry, returning, location }) {
  const { style, en } = useSettings(),
    navigate = useNavigate(),
    scope = useRequestScope(user);
  const isAdmin = Boolean(user?.id && user.role === 'ADMIN');
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '',
    initial = feedInitialPage(params);
  const [input, setInput] = useState(query),
    [composing, setComposing] = useState(false),
    [scale, setScale] = useState(1),
    [restoreEpoch, setRestoreEpoch] = useState(0);
  const deferred = useDeferredValue(input.trim()),
    inputRef = useRef(null),
    more = useRef(null),
    busy = useRef(false);
  const syncing = useRef(null);
  const restore = useRef(returning),
    lastEntry = useRef(entry);
  if (lastEntry.current !== entry) {
    lastEntry.current = entry;
    restore.current = returning;
  }
  // POP/forward navigation restores the URL-backed input without remounting on each keystroke.
  useEffect(() => {
    if (returning) {
      syncing.current = query;
      setInput(query);
    }
  }, [query, returning, location.key]);
  useEffect(() => {
    if (syncing.current !== null) {
      if (deferred === syncing.current) syncing.current = null;
      return;
    }
    if (composing || deferred === query) return;
    const next = new URLSearchParams(params);
    if (deferred) next.set('q', deferred);
    else next.delete('q');
    next.delete('page');
    setParams(next, { replace: true });
  }, [deferred, composing, query, params, setParams]);
  const list = useInfiniteQuery({
    queryKey: ['feed-list60', locale, user?.id || 'guest', entry.version, query, initial, hidden],
    queryFn: async ({ pageParam, signal }) => {
      if (!hidden) return loadFeedPage(pageParam, query, locale, signal);
      const request = scope();
      signal.addEventListener('abort', request.cancel, { once: true });
      if (signal.aborted) request.cancel();
      try {
        const items = await feedsApi.hidden(query, request.options.signal, locale, request.options);
        if (!request.current()) throw Error('Feed session changed');
        return { items, next: undefined };
      } finally {
        signal.removeEventListener('abort', request.cancel);
        request.finish();
      }
    },
    initialPageParam: initial,
    getNextPageParam: (p) => p.next,
    retry: false,
    staleTime: hidden ? 0 : 60000,
    gcTime: hidden ? 0 : 300000,
    refetchOnWindowFocus: hidden,
  });
  const items = uniqueFeeds(
    hidden && list.isError ? [] : list.data?.pages.flatMap((p) => p.items) || [],
    hidden,
  );
  const ready = !list.isPending;
  useEffect(() => {
    const update = () =>
      setScale(parseFloat(getComputedStyle(document.documentElement).fontSize) / 16 || 1);
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
    update();
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);
  useEffect(() => {
    if (!restore.current || !ready) return;
    let cancel,
      alive = true;
    const timer = setTimeout(() => {
      if (!entry.current()) return;
      cancel = restoreSearchScroll(entry.y || 0, () => {
        if (!alive) return;
        restore.current = false;
        setRestoreEpoch((n) => n + 1);
        if (entry.current() && entry.focus)
          Array.from(document.querySelectorAll('a[data-feed-id]'))
            .find((a) => a.getAttribute('href') === entry.focus)
            ?.focus({ preventScroll: true });
      });
    }, 0);
    return () => {
      alive = false;
      clearTimeout(timer);
      cancel?.();
    };
  }, [entry, ready]);
  useEffect(() => {
    const save = () => {
      if (entry.current() && !restore.current) entry.y = window.scrollY;
    };
    window.addEventListener('scroll', save, { passive: true });
    return () => window.removeEventListener('scroll', save);
  }, [entry]);
  const { hasNextPage, isFetching, isError, fetchNextPage } = list;
  const next = async () => {
    if (busy.current || isFetching || !hasNextPage) return;
    busy.current = true;
    try {
      await fetchNextPage({ cancelRefetch: false });
    } finally {
      busy.current = false;
    }
  };
  const nextRef = useRef(next);
  nextRef.current = next;
  useEffect(() => {
    if (
      !more.current ||
      !hasNextPage ||
      isFetching ||
      isError ||
      typeof IntersectionObserver === 'undefined'
    )
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !document.hidden && !restore.current)
          void nextRef.current();
      },
      { rootMargin: '280px' },
    );
    const observe = () => {
      observer.disconnect();
      if (!document.hidden && more.current) observer.observe(more.current);
    };
    observe();
    document.addEventListener('visibilitychange', observe);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', observe);
    };
  }, [hasNextPage, isFetching, isError, restoreEpoch]);
  const title = en ? 'Postpop Feed' : '포스트팝 피드';
  const retry = () => (list.isFetchNextPageError ? next() : list.refetch({ cancelRefetch: false }));
  return (
    <PageLayout paddingTop={{ xs: '16px', lg: '24px' }}>
      <section className="pp-settings pp-feed-list" style={style}>
        <SEO
          title={title}
          description={
            en ? 'Music and culture stories from Postpop' : '포스트팝이 전하는 음악과 문화 이야기'
          }
          url={`${window.location.origin}/feed`}
        />
        {hidden && (
          <Helmet>
            <meta name="robots" content="noindex,nofollow" />
          </Helmet>
        )}
        <header className="pp-settings-header">
          <button
            aria-label={en ? 'Back' : '뒤로'}
            onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/'))}
          >
            <FiChevronLeft size={22} />
          </button>
          <h1>{title}</h1>
          <button
            className="pp-feed-refresh"
            aria-label={en ? 'Refresh list' : '목록 새로고침'}
            disabled={isFetching}
            onClick={() => list.refetch({ cancelRefetch: false })}
          >
            <FiRefreshCw size={18} />
          </button>
        </header>
        {isAdmin && (
          <div className="pp-feed-visibility">
            <button
              type="button"
              aria-pressed={hidden}
              onClick={() => {
                const next = new URLSearchParams(params);
                if (hidden) next.delete('hidden');
                else next.set('hidden', '1');
                next.delete('page');
                setParams(next);
              }}
            >
              <FiEyeOff size={17} aria-hidden="true" />
              {en ? 'Show hidden feeds' : '숨긴 피드 보기'}
            </button>
            {hidden && (
              <p>
                {en
                  ? 'Hidden feeds are visible only to administrators. Open a post to preview it.'
                  : '관리자에게만 보이는 숨긴 피드입니다. 게시물을 눌러 미리보세요.'}
              </p>
            )}
          </div>
        )}
        <form
          className="pp-feed-search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            if (composing || e.nativeEvent.isComposing) return;
            inputRef.current?.blur();
          }}
        >
          <FiSearch size={18} strokeWidth={1.9} aria-hidden="true" />
          <input
            ref={inputRef}
            aria-label={en ? 'Search the Postpop Feed' : '포스트팝 피드 검색'}
            placeholder={en ? 'Search the feed' : '피드 검색'}
            value={input}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="search"
            onChange={(e) => setInput(e.target.value)}
            onCompositionStart={() => setComposing(true)}
            onCompositionEnd={(e) => {
              setInput(e.currentTarget.value);
              setComposing(false);
            }}
          />
          {input && (
            <button
              type="button"
              aria-label={en ? 'Clear search' : '검색어 지우기'}
              onClick={() => {
                setComposing(false);
                setInput('');
                inputRef.current?.focus();
              }}
            >
              <FiX size={17} />
            </button>
          )}
        </form>
        {list.isPending && (
          <p className="pp-feed-state" role="status">
            {en ? 'Loading' : '불러오는 중'}
          </p>
        )}
        <div className="pp-feed-list-grid">
          {items.map((post) => (
            <FeedListCard
              key={post.id}
              {...{ post, en, scale }}
              onOpen={(e) => {
                if (entry.current()) {
                  entry.y = window.scrollY;
                  entry.focus = e.currentTarget.getAttribute('href');
                }
              }}
            />
          ))}
        </div>
        {isError && (
          <div className="pp-feed-state" role="alert">
            <p>{en ? 'We could not load the feed.' : '피드 목록을 불러오지 못했습니다.'}</p>
            <button disabled={isFetching} onClick={retry}>
              {en ? 'Try again' : '다시 시도'}
            </button>
          </div>
        )}
        {!list.isPending && !isError && !items.length && (
          <div className="pp-feed-state" role="status">
            <h2>
              {hidden
                ? en
                  ? 'No hidden feeds'
                  : '숨긴 피드가 없습니다'
                : en
                  ? 'No feed posts yet'
                  : '등록된 피드가 없습니다'}
            </h2>
            <p>
              {en ? 'New stories will appear here.' : '새로운 이야기가 등록되면 이곳에 표시됩니다.'}
            </p>
          </div>
        )}
        {hasNextPage && (
          <div ref={more} className="pp-feed-more">
            <button disabled={isFetching} onClick={next}>
              {isFetching ? (en ? 'Loading' : '불러오는 중') : en ? 'More' : '더 보기'}
            </button>
          </div>
        )}
      </section>
    </PageLayout>
  );
}
